import { useEffect, useRef, useState, useCallback, memo, useMemo, Fragment } from 'react';
import type { Message } from '../../api/model';
import { chatMessageLocalDayKey, formatChatDayDividerLabel } from 'utils/dateUtils';
import MessageItem from '../MessageItem';
import styles from './MessageList.module.scss';

interface MessageListProps {
  messages: Message[];
  onSavePrompt: (content: string) => void;
  containerRef: React.RefObject<HTMLElement | null>;
  projectId?: string;
  chatId?: string | null;
}

const MessageList = memo(
  ({ messages, onSavePrompt, containerRef, projectId, chatId }: MessageListProps) => {
    const isFirstRender = useRef(true);
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isUserScrollingRef = useRef(false);

    const checkIfAtBottom = useCallback(() => {
      if (!containerRef?.current) return true;
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      return scrollHeight - scrollTop - clientHeight < 50;
    }, [containerRef]);

    useEffect(() => {
      const container = containerRef?.current;
      if (!container) return;
      const handleScroll = () => {
        isUserScrollingRef.current = true;
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false;
        }, 150);
        setIsUserAtBottom(checkIfAtBottom());
      };
      container.addEventListener('scroll', handleScroll);
      return () => {
        container.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      };
    }, [containerRef, checkIfAtBottom]);

    useEffect(() => {
      if (containerRef?.current && isFirstRender.current && messages.length > 0) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
        isFirstRender.current = false;
      }
    }, [messages.length, containerRef]);

    useEffect(() => {
      if (isUserScrollingRef.current) return;
      if (isUserAtBottom && containerRef?.current) {
        requestAnimationFrame(() => {
          if (containerRef?.current && !isUserScrollingRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
          }
        });
      }
    }, [messages, isUserAtBottom, containerRef]);

    const reversedMessages = useMemo(() => [...messages].reverse(), [messages]);

    return (
      <div className={styles.list}>
        {reversedMessages.map((message, i) => {
          const prev = i > 0 ? reversedMessages[i - 1] : null;
          const dayCur = message.created_at ? chatMessageLocalDayKey(message.created_at) : null;
          const dayPrev = prev?.created_at ? chatMessageLocalDayKey(prev.created_at) : null;
          const showDayDivider = i > 0 && dayCur != null && dayPrev != null && dayCur !== dayPrev;

          return (
            <Fragment key={`chat-row-${messages.length}-${i}`}>
              {showDayDivider && message.created_at ? (
                <div className={styles.dayDivider} role="separator">
                  <span className={styles.dayDividerLabel}>
                    {formatChatDayDividerLabel(message.created_at)}
                  </span>
                </div>
              ) : null}
              <MessageItem
                message={message}
                onSavePrompt={onSavePrompt}
                projectId={projectId}
                chatId={chatId}
              />
            </Fragment>
          );
        })}
      </div>
    );
  },
  (prevProps, nextProps) =>
    prevProps.messages.length === nextProps.messages.length &&
    prevProps.messages.every((msg, idx) => {
      const nextMsg = nextProps.messages[idx];
      return (
        msg.content === nextMsg.content &&
        msg.role === nextMsg.role &&
        msg.created_at === nextMsg.created_at &&
        msg.progressLabel === nextMsg.progressLabel &&
        msg.isStreaming === nextMsg.isStreaming
      );
    }) &&
    prevProps.onSavePrompt === nextProps.onSavePrompt &&
    prevProps.containerRef === nextProps.containerRef &&
    prevProps.projectId === nextProps.projectId &&
    prevProps.chatId === nextProps.chatId
);

MessageList.displayName = 'MessageList';

export default MessageList;
